import React, { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Alert,
    CircularProgress,
    Skeleton
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useForm, FormProvider } from 'react-hook-form';
import useSWR, { mutate } from 'swr';
import CaseInfoForm from './CaseInfoForm';
import PetitionerRespondentForm from './PetitionerRespondentForm';
import ChildrenSection from './ChildrenSection';
import AddressesSection from './AddressesSection';
import FinancialInfoSection from './FinancialInfoSection';
import { useGetCaseDetails, useUpdateCaseDetails } from '../../api/casedetails';
import { useChannelSubscription } from '../../hooks/useAppSyncSubscription';
import UserProfileContext from '../../context/userprofilecontext';

// Fetcher function for SWR
// const fetcher = (url) => fetch(url).then((res) => res.json());

// Default form values
const defaultFormValues = {
    jurisdictionState: '',
    courtName: '',
    caseNumber: '',
    petitioner: {
        firstName: '',
        middleName: '',
        lastName: '',
        birthDate: '',
        client: false
    },
    respondent: {
        firstName: '',
        middleName: '',
        lastName: '',
        birthDate: '',
        client: false
    },
    marriageDate: '',
    separationDate: '',
    filingDate: '',
    children: [],
    addresses: [],
    monthlySpousalSupport: '',
    monthlyChildSupport: '',
    clientSelection: ''
};

const CaseMaintenanceScreen = () => {
    // Get caseId from URL params
    //const { caseId } = useParams();

    // React Router v6 navigation hook
    const navigate = useNavigate();

    const { data: caseDetails, error, isLoading } = useGetCaseDetails()

    // Phase 3b: required-field relaxation. Case Details no longer hard-blocks
    // save on missing fields — interview-driven population is incremental and
    // the agent must be able to write partial state. Per-field `rules` on
    // the child forms keep data-correctness checks (date relationships,
    // length minimums, character patterns) firing only when a value IS
    // provided. Empty form, partial form, fully-filled form — all save.
    const methods = useForm({
        defaultValues: defaultFormValues,
        mode: 'onChange',
    });

    const { handleSubmit, reset, formState: { errors, isDirty } } = methods;

    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState(null);
    const [saveSuccess, setSaveSuccess] = React.useState(false);

    // Load data when it's fetched into react-hook-form. Refined for Phase 3d:
    // when the agent writes via update_case_details, an AppSync event triggers
    // mutate('CASE_DETAILS') and SWR re-fetches — the new caseDetails object
    // would otherwise blow away any in-progress user edits via reset(). Now we
    // skip the auto-reset when the form is dirty and instead surface a
    // "Refresh" banner so the user can choose whether to discard their edits.
    const hasLoadedOnce = React.useRef(false);
    const lastResetCaseDetailsRef = React.useRef(null);
    const [remoteUpdated, setRemoteUpdated] = React.useState(false);

    const applyCaseDetailsToForm = React.useCallback((cd) => {
        const transformed = cd.petitioner?.client
            ? { ...cd, clientSelection: 'petitioner' }
            : cd.respondent?.client
                ? { ...cd, clientSelection: 'respondent' }
                : cd;
        reset(transformed, {
            keepErrors: false,
            keepIsSubmitting: false,
            keepDirty: false
        });
        lastResetCaseDetailsRef.current = cd;
        setRemoteUpdated(false);
    }, [reset]);

    React.useEffect(() => {
        if (caseDetails) {
            const isFirstLoadOfThisData = lastResetCaseDetailsRef.current !== caseDetails;
            // No new server data — nothing to apply. Without this guard, an
            // isDirty flip (false→true on first keystroke) re-runs the effect
            // and falls through to applyCaseDetailsToForm, which resets the
            // form to server values via reset({ keepDirty: false }) — wiping
            // the keystroke and flipping isDirty back to false. Next
            // keystroke restarts the loop, so the form flickers and refuses
            // to accept edits.
            if (!isFirstLoadOfThisData) return;
            if (isDirty) {
                // Don't clobber user edits — surface a refresh affordance.
                setRemoteUpdated(true);
                return;
            }
            applyCaseDetailsToForm(caseDetails);
        } else if (!isLoading && !hasLoadedOnce.current) {
            // First load with no data - ensure form is in a clean valid state
            reset(defaultFormValues);
            hasLoadedOnce.current = true;
        }
    }, [caseDetails, isLoading, isDirty, applyCaseDetailsToForm, reset]);

    // Force re-validation after caseDetails is loaded (not on every change).
    // Uses useRef to track initial mount + data load so we don't spam trigger().
    const hasTriggeredOnce = React.useRef(false);

    React.useEffect(() => {
        if (!hasTriggeredOnce.current && !isLoading) {
            methods.trigger();
            hasTriggeredOnce.current = true;
        }
    }, [caseDetails, isLoading]);

    // Listen for agent-driven Case Details writes (Phase 3d's
    // update_case_details tool publishes a 'caseDetails' AppSync event keyed
    // on the active case_id when it commits). On event, ask SWR to revalidate
    // — the load effect above then either updates the form (if clean) or
    // surfaces the refresh banner (if dirty). No-op on un-cased contexts.
    const { userProfile } = useContext(UserProfileContext);
    const activeCaseId = userProfile?.currentCase?.caseID || null;
    useChannelSubscription(
        activeCaseId,
        'caseDetails',
        () => {
            mutate('CASE_DETAILS');
        },
        { enabled: !!activeCaseId },
    );

    const handleAcceptRemoteRefresh = () => {
        if (caseDetails) {
            applyCaseDetailsToForm(caseDetails);
        }
    };

    const updateCaseDetails = useUpdateCaseDetails();
    const onSubmit = async (formData) => {
        setSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            // Call the update API and wait for it to complete
            await updateCaseDetails(formData);
            setSaveSuccess(true);
            // Re-baseline the form against the just-submitted values so
            // isDirty flips back to false BEFORE the SWR refetch (triggered
            // in useUpdateCaseDetails's finally) arrives. Without this, the
            // load effect sees the new caseDetails ref + still-dirty form
            // and fires the "updated elsewhere (likely by the AI assistant)"
            // banner on every successful save — even for users who can't
            // see the assistant.
            reset(formData);
        } catch (error) {
            setSaveError(error.message || 'Failed to save case details');
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleFormError = (errors) => {
        console.log('Form validation errors:', errors);
        setSaveError('Please fix the validation errors before saving');
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={50} />
                    </Box>

                    {/* Case Info Section */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={200} height={30} sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                        </Box>
                        <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
                    </Box>

                    {/* Petitioner/Respondent Section */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={250} height={30} sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                            <Skeleton variant="rectangular" height={56} sx={{ flex: 1 }} />
                        </Box>
                    </Box>

                    {/* Children Section */}
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={150} height={30} sx={{ mb: 2 }} />
                        <Skeleton variant="rectangular" height={100} />
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Skeleton variant="rectangular" width={100} height={42} />
                        <Skeleton variant="rectangular" width={180} height={42} />
                    </Box>
                </Paper>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Error loading case details: {error.message}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4" component="h1">
                        {caseDetails ? 'Manage Case Details' : 'New Case Details'}
                    </Typography>
                </Box>

                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Case details saved successfully!

                    </Alert>
                )}

                {saveError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {saveError}
                    </Alert>
                )}

                {Object.keys(errors).length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Please fix the highlighted issues before saving
                    </Alert>
                )}

                {remoteUpdated && (
                    <Alert
                        severity="info"
                        sx={{ mb: 2 }}
                        action={
                            <Button color="inherit" size="small" onClick={handleAcceptRemoteRefresh}>
                                Refresh
                            </Button>
                        }
                    >
                        Case details were updated elsewhere (likely by the AI assistant).
                        Refresh to see the latest, or keep editing to save your version.
                    </Alert>
                )}

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit, handleFormError)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <CaseInfoForm />
                            <PetitionerRespondentForm />
                            <ChildrenSection />
                            <AddressesSection />
                            <FinancialInfoSection />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    disabled={saving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                                    disabled={saving || Object.keys(errors).length > 0}
                                >
                                    {saving ? 'Saving...' : 'Save Case Details'}
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </FormProvider>
            </Paper>
        </Container>
    );
};

export default CaseMaintenanceScreen;